import { useGetUserMemberships, useHasCustomerAccount } from "@/api";
import { TypographyBase } from "@/components/typography/Base";
import { TypographyMuted } from "@/components/typography/Muted";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { LoadingCircle } from "@/components/ui/loadingCircle";
import {
  OrganizationSelect,
  NoCustomerAccount
} from "@/features/subscription-offer";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

interface Props {
  offerId: string;
  isPaidOffer: boolean;
  loadingSubmit: boolean;
}

function OrganizationSection({ offerId, isPaidOffer, loadingSubmit }: Props) {
  const [selectedOrganizationSlug, setSelectedOrganizationSlug] =
    useState<string>();

  const { data: memberships } = useGetUserMemberships();

  const { data: hasCustomerAccountData, loading: loadingHasCustomerAccount } =
    useHasCustomerAccount({
      organizationSlug: selectedOrganizationSlug || "",
      skip: !isPaidOffer
    });

  const displayNoCustomerAccount = Boolean(
    selectedOrganizationSlug &&
      isPaidOffer &&
      hasCustomerAccountData?.organization.__typename === "Organization" &&
      !hasCustomerAccountData?.organization.hasCustomerAccount
  );

  return (
    <>
      <TypographyBase className="py-1 border-b dark:border-brand-gray-600 mt-4">
        Subscription details
      </TypographyBase>
      <div className="grid grid-cols-[30ch_1fr] justify-items-end">
        <div>
          <Label className="pt-[11px]" htmlFor="organization">
            Organization
          </Label>
          <TypographyMuted className="dark:text-brand-gray-500">
            The offer will be accepted on behalf of the selected organization
          </TypographyMuted>
        </div>
        <div className="w-full max-w-80">
          <OrganizationSelect
            memberships={memberships}
            setSelectedOrganizationSlug={setSelectedOrganizationSlug}
          />
        </div>
        <Link
          to="/organization-create"
          search={{
            redirect: {
              to: "/standing-offer/$standingOfferId",
              params: { standingOfferId: offerId }
            }
          }}
          className="place-self-start mt-4"
        >
          <Button variant="outline">Create new organization</Button>
        </Link>
      </div>
      {displayNoCustomerAccount && selectedOrganizationSlug && (
        <NoCustomerAccount organizationSlug={selectedOrganizationSlug} />
      )}
      {!displayNoCustomerAccount && (
        <div className="flex justify-end items-center gap-3 mt-8">
          {loadingSubmit && <LoadingCircle />}
          <Button
            variant="default"
            type="submit"
            data-testid="acceptButton"
            disabled={
              memberships.length < 1 ||
              loadingHasCustomerAccount ||
              !selectedOrganizationSlug
            }
          >
            {isPaidOffer ? "Go to checkout" : "Accept offer"}
          </Button>
        </div>
      )}
    </>
  );
}

export default OrganizationSection;
